import { Component, OnInit } from '@angular/core';
import { NavbarComponent } from '../navbar/navbar.component';
import { RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RestService } from '../service/rest/rest.service';
import { AuthService } from '../service/auth/auth.service';
import { Subject } from '../model/subject';
import { Course } from '../model/course';
import { LoggerService } from '../service/utils/logger.service';
import { QuestionResultsListComponent } from '../components/question-results-list/question-results-list.component';
import { QuestionDetailModalComponent } from '../components/question-detail-modal/question-detail-modal.component';

export interface SearchResult {
  id: number;
  questionHtml: string;
  answer1?: string;
  answer2?: string;
  answer3?: string;
  answer4?: string;
  correctAnswer?: string | null;
  explanationHtml?: string;
  subjectName: string;
  matchedField: string;
  matchedText: string;
}

@Component({
  selector: 'app-search-question',
  standalone: true,
  imports: [NavbarComponent, RouterLink, ReactiveFormsModule, CommonModule, QuestionResultsListComponent, QuestionDetailModalComponent],
  templateUrl: './search-question.component.html',
  styleUrl: './search-question.component.css'
})
export class SearchQuestionComponent implements OnInit {
  searchForm: FormGroup;
  courses: Course[] = [];
  subjects: Subject[] = [];
  selectedCourseId: string | null = null;
  searchResults: SearchResult[] = [];
  resultDisplayHtmlById: Record<number, string> = {};
  isLoading = false;
  hasSearched = false;
  searchError = '';

  // Detail modal
  detailOpen: boolean = false;
  detailQuestionId: number | null = null;
  detailQuestionHtml: string | null = null;
  detailAnswersHtml: Array<string | null> = [];
  detailCorrectAnswer: string | null = null;

  constructor(
    private fb: FormBuilder,
    private restService: RestService,
    private authService: AuthService
  ) {
    this.searchForm = this.fb.group({
      courseId: [''],
      subjectId: [''],
      question: [''],
      answer: [''],
      explanation: [''],
      questionNumber: ['']
    });
  }

  openDetail(questionId: number) {
    this.detailQuestionId = questionId;
    const r = this.searchResults.find(x => x.id === questionId);
    this.detailQuestionHtml = r?.questionHtml || null;
    this.detailAnswersHtml = [r?.answer1 || null, r?.answer2 || null, r?.answer3 || null, r?.answer4 || null];
    this.detailCorrectAnswer = (r?.correctAnswer || null) as any;
    this.detailOpen = true;
  }

  closeDetail() {
    this.detailOpen = false;
    this.detailQuestionId = null;
    this.detailQuestionHtml = null;
    this.detailAnswersHtml = [];
    this.detailCorrectAnswer = null;
  }

  async ngOnInit() {
    await this.loadCourses();
  }

  async loadCourses() {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      LoggerService.log('Loading courses, sessionId:', sessionId);
      
      if (!sessionId) {
        LoggerService.error('User not authenticated when loading courses', null);
        return;
      }
      
      const courses = await this.restService.getCourses(sessionId);
      LoggerService.log('Courses received from service:', courses);
      
      if (courses && courses.length > 0) {
        this.courses = courses;
        LoggerService.log(`Successfully loaded ${courses.length} courses into component`);
        LoggerService.log('Courses array:', this.courses);
      } else {
        LoggerService.log('No courses returned from API or empty array');
        this.courses = [];
      }
    } catch (error) {
      LoggerService.error('Failed to load courses:', error);
      this.courses = [];
    }
  }

  async onCourseChange() {
    const courseIdValue = this.searchForm.get('courseId')?.value;
    this.selectedCourseId = courseIdValue;
    this.searchForm.patchValue({ subjectId: '' });
    this.subjects = [];

    if (courseIdValue && courseIdValue !== '') {
      try {
        const user = this.authService.getUser();
        const sessionId = user?.sid;
        if (sessionId) {
          // Convert string courseId to number
          const courseId = parseInt(courseIdValue);
          if (!isNaN(courseId)) {
            const subjects = await this.restService.getSubjects(sessionId, courseId);
            if (subjects) {
              this.subjects = subjects;
              LoggerService.log(`Loaded ${subjects.length} subjects for course ${courseId}`);
            } else {
              LoggerService.log('No subjects returned from API');
            }
          }
        } else {
          LoggerService.error('User not authenticated when loading subjects', null);
        }
      } catch (error) {
        LoggerService.error('Failed to load subjects:', error);
      }
    }
  }

  async onSearch() {
    // Allow search even if form is "invalid" - we just need at least one search criteria
    const hasSearchCriteria = 
      this.searchForm.get('courseId')?.value ||
      this.searchForm.get('subjectId')?.value ||
      this.searchForm.get('question')?.value?.trim() ||
      this.searchForm.get('answer')?.value?.trim() ||
      this.searchForm.get('explanation')?.value?.trim() ||
      this.searchForm.get('questionNumber')?.value?.trim();
    
    if (!hasSearchCriteria) {
      // Show message that at least one search criteria is required
      return;
    }

    this.isLoading = true;
    this.hasSearched = true;
    this.searchError = '';
    this.searchResults = [];
    this.resultDisplayHtmlById = {};

    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) {
        LoggerService.error('User not authenticated', null);
        return;
      }

      const courseIdValue = this.searchForm.get('courseId')?.value;
      const subjectIdValue = this.searchForm.get('subjectId')?.value;
      
      // Convert string IDs to numbers, handling empty strings
      let courseId: number | null = null;
      if (courseIdValue && courseIdValue !== '') {
        const parsed = parseInt(courseIdValue);
        courseId = isNaN(parsed) ? null : parsed;
      }
      
      let subjectId: number | null = null;
      if (subjectIdValue && subjectIdValue !== '') {
        const parsed = parseInt(subjectIdValue);
        subjectId = isNaN(parsed) ? null : parsed;
      }
      
      const searchParams = {
        courseId: courseId,
        subjectId: subjectId,
        question: this.searchForm.get('question')?.value?.trim() || null,
        answer: this.searchForm.get('answer')?.value?.trim() || null,
        explanation: this.searchForm.get('explanation')?.value?.trim() || null,
        questionNumber: this.searchForm.get('questionNumber')?.value?.trim() || null
      };
      
      LoggerService.log('Search params:', searchParams);

      const results = await this.restService.searchQuestions(sessionId, searchParams);
      if (results === null && this.restService.lastAccessDenial) {
        this.searchError = this.restService.accessDeniedMessage();
        return;
      }
      if (results) {
        // Map raw results into SearchResult with basic matchedField/matchedText.
        // For now we assume the primary match is in the question text.
        this.searchResults = results.map(r => ({
          id: r.id,
          questionHtml: r.questionHtml,
          answer1: r.answer1 || undefined,
          answer2: r.answer2 || undefined,
          answer3: r.answer3 || undefined,
          answer4: r.answer4 || undefined,
          correctAnswer: (r as any).correctAnswer ?? null,
          explanationHtml: r.explanationHtml || undefined,
          subjectName: r.subjectName,
          matchedField: 'question',
          matchedText: r.questionHtml || ''
        }));
        for (const r of this.searchResults) {
          this.resultDisplayHtmlById[r.id] = this.getHighlightedText(r);
        }
      }
    } catch (error) {
      LoggerService.error('Search failed:', error);
    } finally {
      this.isLoading = false;
    }
  }

  highlightKeyword(text: string, keyword: string): string {
    if (!keyword || !text) {
      return this.stripHtml(text || '');
    }
    // Strip HTML tags for search and highlighting, but preserve structure
    const strippedText = this.stripHtml(text);
    const regex = new RegExp(`(${this.escapeRegex(keyword)})`, 'gi');
    return strippedText.replace(regex, '<mark>$1</mark>');
  }
  
  private stripHtml(html: string): string {
    if (!html) return '';
    // Create a temporary div element to parse HTML
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  getMatchedText(result: SearchResult): string {
    return result.matchedText || '';
  }

  getHighlightedText(result: SearchResult): string {
    const keyword = this.getSearchKeyword(result.matchedField);
    const text = this.getMatchedText(result);
    return this.highlightKeyword(text, keyword);
  }

  private getSearchKeyword(field: string): string {
    switch (field) {
      case 'question':
        return this.searchForm.get('question')?.value?.trim() || '';
      case 'answer':
        return this.searchForm.get('answer')?.value?.trim() || '';
      case 'explanation':
        return this.searchForm.get('explanation')?.value?.trim() || '';
      case 'questionNumber':
        return this.searchForm.get('questionNumber')?.value?.trim() || '';
      default:
        return '';
    }
  }

  onResultClick(result: SearchResult) {
    // TODO: Define target behavior - could navigate to question detail page
    LoggerService.log('Clicked on result:', result);
  }
}
